import React, { useState } from 'react';
import { Sparkles, Check, Crown, TrendingUp, Shield, Bell, Loader } from 'lucide-react';
import Logo from './Logo';

const Landing = ({ onShowLogin, onShowSignUp }) => {
  const [demoQuery, setDemoQuery] = useState('');
  const [demoResponse, setDemoResponse] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoUsed, setDemoUsed] = useState(false);
  const [showRateLimit, setShowRateLimit] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  // Pre-generated example responses
  const examples = [
    {
      question: "Does my Xfinity internet include free streaming?",
      answer: "Yes! Xfinity Internet customers get Peacock Premium included free - that's a $5.99/month value. Peacock includes thousands of hours of TV shows, movies, live sports (Sunday Night Football, Premier League), and originals.\n\nTo activate: Log into your Xfinity account, go to the Apps section, and link your Peacock account. If you're already paying for Peacock separately, you can cancel that subscription and save $72/year.\n\nXfinity also offers other streaming bundles on certain plans - I'd need to know your specific plan to tell you what else you might be eligible for."
    },
    {
      question: "What streaming perks do I get with my American Express Platinum card?",
      answer: "Your Amex Platinum ($695 annual fee) includes impressive streaming benefits:\n\n**Monthly Streaming Credits:** Up to $20/month ($240/year total) that can cover Netflix, Disney+, Hulu, Peacock, ESPN+, and more. This alone covers 1/3 of your annual fee.\n\n**Walmart+ Membership:** $98/year value included free, which itself gives you a choice of Paramount+ Essential OR Peacock Premium at no extra cost.\n\n**Total streaming value:** $338/year in direct streaming benefits, plus the Walmart+ membership. Make sure you've enrolled in these benefits through your Amex account - many cardholders don't realize they have them!"
    },
    {
      question: "What's the best bundle deal for Netflix, Disney+, and Hulu?",
      answer: "The Disney Bundle is your best option: **Disney+ (No Ads) + Hulu (No Ads) + ESPN+ for $24.99/month**.\n\nCompared to buying separately:\n- Disney+ (No Ads): $13.99\n- Hulu (No Ads): $17.99  \n- ESPN+: $10.99\n- **Total if separate: $42.97**\n- **Bundle price: $24.99**\n- **Your savings: $17.98/month = $215.76/year**\n\nNote: Netflix doesn't bundle with other services. But check if you're getting Netflix free through T-Mobile (Magenta Max plan includes Netflix Standard) or with streaming credits from premium credit cards like Amex Platinum."
    }
  ];

  const handleExampleClick = (example) => {
    setDemoQuery(example.question);
    setDemoResponse(example.answer);
    setDemoUsed(true);
  };

  const handleCustomQuery = async () => {
    if (!demoQuery.trim()) return;
    
    // Check sessionStorage
    const sessionUsed = sessionStorage.getItem('demo_used');
    if (sessionUsed) {
      setShowRateLimit(true);
      return;
    }

    setDemoLoading(true);
    setDemoResponse('');
    setShowRateLimit(false);

    try {
      const response = await fetch('/api/demo-query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: demoQuery })
      });

      const data = await response.json();

      if (response.status === 429) {
        // Rate limited
        setShowRateLimit(true);
        sessionStorage.setItem('demo_used', 'true');
      } else if (response.ok) {
        setDemoResponse(data.response);
        setDemoUsed(true);
        sessionStorage.setItem('demo_used', 'true');
      } else {
        setDemoResponse('Sorry, something went wrong. Please try again or sign up for a free account!');
      }
    } catch (error) {
      console.error('Demo query error:', error);
      setDemoResponse('Sorry, something went wrong. Please try again or sign up for a free account!');
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex justify-between items-center">
        <Logo size="default" variant="glow" />
        <button
          onClick={onShowLogin}
          className="px-6 py-2 bg-white text-purple-900 rounded-lg hover:bg-purple-50 transition-colors font-bold"
        >
          Log In
        </button>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Never forget. Never overpay. 🐘
        </h1>
        <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-3xl mx-auto">
          Your streaming subscriptions, optimized by AI. Find hidden perks, catch every deal, and stop overpaying.
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={onShowSignUp}
            className="px-10 py-5 bg-white text-purple-900 rounded-xl hover:bg-purple-50 transition-all font-bold text-xl shadow-xl hover:shadow-2xl hover:scale-105 transform"
          >
            Get Started Free →
          </button>
        </div>
      </section>

      {/* Demo Section */}
      <section className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <Sparkles className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Try It Now - No Account Needed
            </h2>
            <p className="text-gray-600">
              See what Stomp can do for you. Click an example or ask your own question.
            </p>
          </div>

          {/* Example Questions */}
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            {examples.map((example, i) => (
              <button
                key={i}
                onClick={() => handleExampleClick(example)}
                className="p-4 text-left bg-purple-50 hover:bg-purple-100 rounded-lg text-sm text-gray-700 transition-colors border-2 border-transparent hover:border-purple-300"
              >
                {example.question}
              </button>
            ))}
          </div>

          {/* Custom Query Input */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-2 text-center font-medium">
              Or ask your own question:
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={demoQuery}
                onChange={(e) => setDemoQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCustomQuery()}
                placeholder="e.g., 'Does my Chase Sapphire Reserve include streaming perks?'"
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={demoLoading}
              />
              <button
                onClick={handleCustomQuery}
                disabled={demoLoading || !demoQuery.trim()}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                {demoLoading ? <Loader className="w-5 h-5 animate-spin" /> : 'Ask'}
              </button>
            </div>
          </div>

          {/* Demo Response */}
          {demoResponse && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="prose prose-purple max-w-none">
                <p className="text-gray-800 whitespace-pre-wrap">{demoResponse}</p>
              </div>
            </div>
          )}

          {/* Rate Limit Message */}
          {showRateLimit && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
              <h3 className="text-xl font-bold text-purple-900 mb-2">
                Want to ask more questions?
              </h3>
              <p className="text-gray-700 mb-4">
                Sign up for a free account and get <strong>5 AI queries per month</strong> - plus save your profile for personalized recommendations!
              </p>
              <button
                onClick={onShowSignUp}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-bold"
              >
                Sign Up Free
              </button>
            </div>
          )}

          {/* CTA after demo */}
          {demoUsed && !showRateLimit && (
            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-gray-700 mb-3">
                Like what you see? <strong>Sign up free</strong> to save your profile and get 5 queries per month!
              </p>
              <button
                onClick={onShowSignUp}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-bold"
              >
                Create Free Account
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16 max-w-6xl">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          Choose Your Plan
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Free Tier */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <Shield className="w-12 h-12 text-purple-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Free Forever</h3>
              <p className="text-4xl font-bold text-purple-600 mb-1">$0</p>
              <p className="text-gray-600">No credit card required</p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700"><strong>5 AI queries per month</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Credit card & membership perk checking</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Find where to watch any show</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Bundle recommendations</span>
              </li>
            </ul>

            <button
              onClick={onShowSignUp}
              className="w-full px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors font-bold"
            >
              Sign Up Free
            </button>
          </div>

          {/* Premium Tier */}
          <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl shadow-xl p-8 text-white relative">
          
            
            <div className="text-center mb-6">
              <Crown className="w-12 h-12 text-yellow-300 mx-auto mb-3" />
              <h3 className="text-2xl font-bold mb-2">Premium</h3>
              <p className="text-4xl font-bold mb-1">$4.99<span className="text-xl">/mo</span></p>
              <p className="text-purple-200">Less than one unused subscription!</p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span><strong>Unlimited AI queries</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span>Real-time perk & deal alerts</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span>Expiration reminders (never miss a benefit)</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span>Monthly savings reports</span>
              </li>
               <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span>Earn rewards for deal recommendations</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                <span>Priority support</span>
              </li>
            </ul>

            <button
              onClick={() => setShowComingSoon(true)}
              className="w-full px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-bold"
            >
              Start Premium
            </button>
            <p className="text-center text-sm text-purple-200 mt-3">
              7-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-white">
            <div>
              <TrendingUp className="w-12 h-12 mx-auto mb-3 text-purple-300" />
              <p className="text-3xl font-bold mb-2">$200+</p>
              <p className="text-purple-200">Average annual savings per user</p>
            </div>
            <div>
              <Bell className="w-12 h-12 mx-auto mb-3 text-purple-300" />
              <p className="text-3xl font-bold mb-2">24/7</p>
              <p className="text-purple-200">Monitoring for new perks & deals</p>
            </div>
            <div>
              <Shield className="w-12 h-12 mx-auto mb-3 text-purple-300" />
              <p className="text-3xl font-bold mb-2">100%</p>
              <p className="text-purple-200">Working for you, not advertisers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-purple-800">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-purple-200 text-sm">
          <div>© 2024 Stomp. Never forget. Never overpay.</div>
          <div className="flex gap-6">
            <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
          </div>
        </div>
      </footer>

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <Crown className="w-16 h-16 text-purple-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Premium Coming Soon! 🚀
            </h2>
            <p className="text-gray-600 mb-6">
              We're putting the finishing touches on Premium subscriptions. Sign up for a free account now and you'll be first to know when Premium launches!
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowComingSoon(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 rounded-lg transition-all"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowComingSoon(false);
                  onShowSignUp();
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition-all"
              >
                Sign Up Free
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;